/**
 * Beeps are synthesised with the Web Audio API rather than shipped as files:
 * no network fetch, no decode latency, nothing to cache-bust, ~0 bytes of asset.
 *
 * Browsers refuse to start an AudioContext outside a user gesture, so `unlock()`
 * must be called from the same keydown/pointerdown that starts the timer.
 */

type Win = Window & { webkitAudioContext?: typeof AudioContext };

let ctx: AudioContext | null = null;

export function unlock(): void {
  if (!ctx) {
    const Ctor = window.AudioContext ?? (window as Win).webkitAudioContext;
    if (!Ctor) return;
    try {
      ctx = new Ctor();
    } catch {
      return;
    }
  }
  if (ctx.state === "suspended") void ctx.resume();
}

export function isReady(): boolean {
  return ctx !== null && ctx.state === "running";
}

/**
 * One sine blip. Ramped in and out so it does not click, which a bare
 * start/stop on an oscillator always does.
 */
function blip(offset: number, freq: number, seconds: number, peak = 0.22): void {
  if (!ctx) return;
  const t0 = ctx.currentTime + offset;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, t0);

  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.014);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + seconds);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + seconds + 0.03);
}

/** Five minutes remaining — one short, unobtrusive note. */
export function beepFiveMinutes(): void {
  blip(0, 880, 0.18);
}

/** One minute remaining — same note, twice, so the two are distinguishable. */
export function beepOneMinute(): void {
  blip(0, 880, 0.16);
  blip(0.26, 880, 0.16);
}

/** Zero — a three-note flourish, louder and longer than the warnings. */
export function alarm(): void {
  blip(0, 988, 0.2, 0.3);
  blip(0.3, 988, 0.2, 0.3);
  blip(0.6, 1319, 0.55, 0.32);
}
